/**
 * SDK for visearch of visenze.com
 * @author dejun@visenze.com, @author rachel.ngo@visenze.com
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import va from 'visenze-tracking-javascript';
import { version } from './version.js';
import { searchById, searchByImage, searchByIdByPost, multisearch, multisearchAutocomplete } from './productsearch.js';
import { resizeImageFromDataUrl } from './resizer.js';
const STAGING_ENDPOINT = 'https://search-dev.visenze.com';
const ANALYTICS_STAGING_ENDPOINT = 'https://staging-analytics.data.visenze.com/v3';
const SDK = 'visearch js sdk';
const SDK_VERSION = version;
const RESULT_LOAD = 'result_load';
function getSdkVersion() {
    return {
        v: SDK_VERSION,
        sdk: SDK,
    };
}
export function ViSearch(configs) {
    const q = [];
    const settings = {
        app_key: '',
        placement_id: '',
    };
    let lastQueryId;
    let tracker;
    Object.defineProperty(q, 'push', (args) => prototypes.applyPrototypesCall(args));
    if (configs) {
        Object.entries(configs).forEach(([key, value]) => {
            settings[key] = value;
        });
    }
    function getTracker() {
        if (!tracker) {
            const code = `${settings.app_key}:${settings.placement_id}`;
            const endpoint = settings.analytics_endpoint ||
                (settings.endpoint === STAGING_ENDPOINT ? ANALYTICS_STAGING_ENDPOINT : undefined);
            const isCN = !!settings['is_cn'];
            tracker = va({
                code,
                uid: settings.uid,
                endpoint,
                isCN,
            });
        }
        return tracker;
    }
    /**
     * if event is not provided, we send the default tracking params;
     * which includes: sdk, sid, ts, uid and version
     * @returns tracking parameters
     */
    function getDefaultTrackingParams() {
        tracker = getTracker();
        const trackerParams = tracker.getDefaultTrackingParams();
        if (tracker) {
            return Object.assign(Object.assign({}, trackerParams), getSdkVersion());
        }
        return {};
    }
    function sendResultLoadEvent(productId, resp) {
        if (resp.status !== 'OK') {
            return;
        }
        else if (resp.objects && resp.objects.length === 0) {
            return;
        }
        else if (resp.result && resp.result.length === 0) {
            return;
        }
        const metadata = { queryId: resp.reqid };
        if (productId) {
            metadata['pid'] = productId;
        }
        // send out event if the pixel is in place
        if (window && window.vsPlacementLoaded && window.vsPlacementLoaded[settings.placement_id]) {
            prototypes.sendEvent(RESULT_LOAD, metadata);
        }
        // push result_load event to GTM datalayer if user enable GTM tracking
        if (settings['gtm_tracking']) {
            if (!window.dataLayer) {
                window.dataLayer = [];
            }
            const data = { event: 'vs_result_load' };
            const params = { queryId: resp.reqid };
            if (productId) {
                params['pid'] = productId;
            }
            data[settings.placement_id] = params;
            window.dataLayer.push(data);
        }
    }
    /**
     * Save the query Id of the last request to local storage if the request is successfull
     * @param {*} resp response from ProductSearch API
     */
    function saveQueryId(resp) {
        if (resp.status !== 'OK') {
            return;
        }
        lastQueryId = resp.reqid;
        localStorage.setItem(`visenze_query_id_${settings.placement_id}`, lastQueryId);
    }
    /**
     * Wrapping response with addition method for abtesting tracking
     */
    function wrapExperimentResponse(resp) {
        if (resp.status !== 'OK') {
            return;
        }
        if (resp.experiment && resp.experiment.experiment_no_recommendation) {
            resp.experiment_no_recommendation = true;
        }
    }
    /**
     * Wrapper for callback with additional send result_load event and new method to track a/b test
     * @param {*} resp response from ProductSearch API
     */
    function wrapCallback(productId, callback, resp) {
        wrapExperimentResponse(resp);
        callback === null || callback === void 0 ? void 0 : callback(resp);
        saveQueryId(resp);
        sendResultLoadEvent(productId, resp);
    }
    function wrapCallbackAutoComplete(callback, resp) {
        callback === null || callback === void 0 ? void 0 : callback(resp);
    }
    const prototypes = {
        q,
        set: function (key, value) {
            settings[key] = value;
        },
        setKeys: function (configs) {
            Object.entries(configs).forEach(([key, value]) => {
                settings[key] = value;
            });
        },
        sendEvent: function (action, eventParams, callback, failure) {
            const tracker = getTracker();
            const params = Object.assign(Object.assign({}, getSdkVersion()), eventParams);
            if (tracker) {
                tracker.sendEvent(action, params, () => {
                    callback === null || callback === void 0 ? void 0 : callback();
                }, (err) => {
                    failure === null || failure === void 0 ? void 0 : failure(err);
                });
            }
        },
        sendEvents: function (action, eventParamsList, callback, failure) {
            const tracker = getTracker();
            if (tracker && tracker.validateEvents(eventParamsList, failure)) {
                const batchId = tracker.generateUUID();
                eventParamsList.forEach((eventParams) => {
                    const params = Object.assign(Object.assign({}, eventParams), getSdkVersion());
                    if (action.toLowerCase() === 'transaction' && !params['transId']) {
                        params['transId'] = batchId;
                    }
                    prototypes.sendEvent(action, params, callback, failure);
                });
            }
        },
        productMultisearch: function (params, callback, failure) {
            const altCallback = wrapCallback.bind(undefined, undefined, callback);
            return multisearch(settings, params, getDefaultTrackingParams(), altCallback, failure);
        },
        productMultisearchAutocomplete: function (params, callback, failure) {
            const altCallback = wrapCallbackAutoComplete.bind(undefined, callback);
            return multisearchAutocomplete(settings, params, getDefaultTrackingParams(), altCallback, failure);
        },
        productSearchByImage: function (params, callback, failure) {
            const altCallback = wrapCallback.bind(undefined, undefined, callback);
            return searchByImage(settings, params, getDefaultTrackingParams(), altCallback, failure);
        },
        productSearchById: function (productId, params, callback, failure) {
            const altCallback = wrapCallback.bind(undefined, productId, callback);
            return searchById(settings, productId, params, getDefaultTrackingParams(), altCallback, failure);
        },
        productRecommendations: function (productId, params, callback, failure) {
            return this.productSearchById(productId, params, callback, failure);
        },
        productRecommendationsByPost: function (productId, params, callback, failure) {
            const altCallback = wrapCallback.bind(undefined, productId, callback);
            return searchByIdByPost(settings, productId, params, getDefaultTrackingParams(), altCallback, failure);
        },
        productSearchByIdByPost: function (productId, params, callback, failure) {
            return this.productRecommendationsByPost(productId, params, callback, failure);
        },
        setUid: function (uid, callback, failure) {
            tracker = getTracker();
            if (tracker) {
                tracker.setUID(uid);
                callback === null || callback === void 0 ? void 0 : callback(uid);
            }
            else {
                failure === null || failure === void 0 ? void 0 : failure(Error('Tracker is not found'));
            }
        },
        getUid: function (callback, failure) {
            const tracker = getTracker();
            if (tracker) {
                callback(tracker.getUID());
            }
            else {
                failure === null || failure === void 0 ? void 0 : failure(Error('Tracker is not found'));
            }
        },
        getSid: function (callback, failure) {
            tracker = getTracker();
            if (tracker) {
                callback(tracker.getSID());
            }
            else {
                failure === null || failure === void 0 ? void 0 : failure(Error('Tracker is not found'));
            }
        },
        getLastQueryId: function (callback, failure) {
            try {
                if (lastQueryId) {
                    callback(lastQueryId);
                }
                else {
                    callback(localStorage.getItem(`visenze_query_id_${settings.placement_id}`));
                }
            }
            catch (err) {
                failure === null || failure === void 0 ? void 0 : failure(err);
            }
        },
        getSessionTimeRemaining: function (callback, failure) {
            tracker = getTracker();
            if (tracker) {
                callback(tracker.getSessionTimeRemaining());
            }
            else {
                failure === null || failure === void 0 ? void 0 : failure(Error('Tracker is not found'));
            }
        },
        resetSession: function (callback, failure) {
            tracker = getTracker();
            if (tracker) {
                const sid = tracker.resetSession();
                callback === null || callback === void 0 ? void 0 : callback(sid);
            }
            else {
                failure === null || failure === void 0 ? void 0 : failure(Error('Tracker is not found'));
            }
        },
        getDefaultTrackingParams: function (callback, failure) {
            tracker = getTracker();
            if (tracker) {
                callback(getDefaultTrackingParams());
            }
            else {
                failure === null || failure === void 0 ? void 0 : failure(Error('Tracker is not found'));
            }
        },
        applyPrototypesCall: function (command) {
            const fnName = command[0];
            const args = command.slice(1);
            if (typeof prototypes[fnName] === 'function') {
                prototypes[fnName](...args);
            }
        },
        resizeImage: function (imageAsDataUrl, resizeSettings, onSuccess, onFailure) {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    const img = yield resizeImageFromDataUrl(imageAsDataUrl, resizeSettings || settings.resize_settings);
                    onSuccess(img);
                }
                catch (err) {
                    onFailure === null || onFailure === void 0 ? void 0 : onFailure(err);
                }
            });
        },
        generateUuid: function (callback, failure) {
            const tracker = getTracker();
            if (tracker) {
                callback(tracker.generateUUID());
            }
            else {
                failure === null || failure === void 0 ? void 0 : failure(Error('Tracker is not found'));
            }
        },
    };
    return prototypes;
}
